import React, { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { Text } from "../../typography/Text";
const msgKey = "durationCounter";

const calculateTotalDurationUnits = (ranges) => {
    if (!ranges || !ranges.length) {
        return null;
    }

    const hasOpenRange = ranges.some((r) => r?.from && !r?.to);
    const now = new Date();

    const totalMs = ranges.reduce((sum, range) => {
        if (!range?.from) {
            return sum;
        }

        const start = new Date(range.from);
        const end = range.to ? new Date(range.to) : now;

        // For fixed durations, ignore seconds completely.
        if (range.to) {
            start.setSeconds(0, 0);
            end.setSeconds(0, 0);
        }

        const diff = end.getTime() - start.getTime();
        return diff > 0 ? sum + diff : sum;
    }, 0);

    if (totalMs <= 0) {
        return null;
    }

    return {
        days: Math.floor(totalMs / (1000 * 60 * 60 * 24)),
        hours: Math.floor((totalMs / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((totalMs / 1000 / 60) % 60),
        seconds: hasOpenRange ? Math.floor((totalMs / 1000) % 60) : 0
    };
};

export const DurationCounter = ({ ranges, emptyValue }) => {
    const [units, setUnits] = useState(null);

    useEffect(() => {
        setUnits(calculateTotalDurationUnits(ranges));

        const isCounting = ranges?.some?.((r) => r?.from && !r?.to);
        if (isCounting) {
            const id = setInterval(() => {
                setUnits(calculateTotalDurationUnits(ranges));
            }, 1000);
            return () => clearInterval(id);
        }
    }, [ranges]);

    if (!units) {
        return <Text color="base40">{emptyValue || <FormattedMessage id={`${msgKey}.noValue`} />}</Text>;
    }

    // Determine which units to display based on progressive logic
    const isCounting = ranges?.some?.((r) => r?.from && !r?.to);

    // Only show non-zero values, unless they come after a non-zero value
    const isActive = {
        days: units.days > 0,
        hours: units.hours > 0 || units.days > 0,
        minutes: (units.minutes > 0 || units.hours > 0 || units.days > 0) && (isCounting || units.minutes > 0),
        seconds: (units.seconds > 0 || units.minutes > 0 || units.hours > 0 || units.days > 0) && isCounting
    };

    // If everything is zero, show something
    const isAllZero =
        !isActive.days &&
        !isActive.hours &&
        (!isActive.minutes || units.minutes === 0) &&
        (!isActive.seconds || units.seconds === 0);

    return (
        <span>
            {isActive.days && (
                <>
                    <FormattedMessage id={`${msgKey}.days`} values={{ days: units.days }} />{" "}
                </>
            )}
            {isActive.hours && (
                <>
                    <FormattedMessage id={`${msgKey}.hours`} values={{ hours: units.hours }} />{" "}
                </>
            )}
            {isActive.minutes && (
                <>
                    <FormattedMessage id={`${msgKey}.minutes`} values={{ minutes: units.minutes }} />{" "}
                </>
            )}
            {isActive.seconds && <FormattedMessage id={`${msgKey}.seconds`} values={{ seconds: units.seconds }} />}
            {isAllZero && <FormattedMessage id={`${msgKey}.zero`} />}
        </span>
    );
};
